import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  FileText,
  ArrowLeftRight,
  Bell,
  AlertTriangle,
  DollarSign,
  Wallet,
  RefreshCw,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Home,
  Briefcase,
  Landmark,
  BarChart3,
  Calendar,
  Moon,
  Percent,
  Clock,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  LabelList,
  Text,
} from 'recharts';
import { formatCompact, formatCurrencyFull } from '../lib/chartFormatters';
import { groupAccountsByBank } from '../lib/bankBalancesGrouping';
import {
  computeGrossRevenueCard, computeNetAssetsCard,
  computeNetRevenueCard, computeMarginCards, computePropertiesCard,
  brosRevenueActual, brosCogsActual,
} from '../lib/entityStatCards';
import { ENTITY_GROSS_REVENUE_SEGMENTS, buildEntityGrossRevenueChartData, buildEntityMarginChartData, computeEntityGrossRevenueYtd } from '../lib/entityGrossRevenueChart';
import { MarginChart } from '../components/dashboard/MarginChart';
import { buildBankBalanceHistoryChartData } from '../lib/entityBankBalanceHistoryChart';
import { BankBalanceHistoryChart } from '../components/dashboard/BankBalanceHistoryChart';
import { buildMonthlyCashflowChartData, computeAvgNetCashflow } from '../lib/monthlyCashflowChart';
import { MonthlyCashflowChart } from '../components/dashboard/MonthlyCashflowChart';
import { buildNetAssetsChartData } from '../lib/netAssetsChart';
import { NetAssetsChart } from '../components/dashboard/NetAssetsChart';
import { BankGroup as SharedBankGroup } from '../components/BankBalanceRows';
import { BankLogo } from '../components/BankLogo';
import { CashRunwayCard } from '../components/dashboard/CashRunwayCard';
import { FxExposureCard } from '../components/dashboard/FxExposureCard';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { TaxReserveCountryMonths } from '../components/TaxReserveCountryMonths';
import { useEntityStore, useAlertsStore, usePortfolioPrefsStore } from '../store';
import { dashboardAPI, alertsAPI, bankAccountsAPI, wealthAPI, portfolioAPI, reportsAPI, assetsLiabilitiesAPI, forecastAPI, bankBalanceHistoryAPI, groupForecastAPI } from '../lib/api';
import { AssetRegisterDialog } from '../components/AssetRegisterDialog';
import { useForecast } from '../hooks/useForecast';
import {
  mergeWithForecast,
  splitForecastSeries,
  ForecastCaveat,
  ForecastBarShape,
  monthKeyToLabel,
  forecastTotalLabelContent,
} from '../lib/forecastChartUtils';
import { ForecastTotalsFooter } from '../components/dashboard/ForecastTotalsFooter';
import { convertAmount } from '../lib/currencyConvert';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { getErrorMessage } from '../lib/errors';
import { JURISDICTION_CONFIG } from '../lib/jurisdictionConfig';
import { SCOPES } from '../store';

import {
  convertCurrencyDisplay as convertCurrency,
  convertBalancesDisplay as convertBalances,
  formatCurrencyShort as formatCurrency,
  CURRENCY_SYMBOLS,
} from '../lib/displayCurrency';

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrencyMap = (map) => {
  const entries = Object.entries(map).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;
  const symbols = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };
  return entries.map(([cur, amt]) => `${symbols[cur] || cur}${amt.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`).join(' · ');
};

const entityTypeColorMap = {
  br_ltd: '#10B981',
  hsr: '#34D399',
  eci: '#3B82F6',
  afpv: '#60A5FA',
  bi_immo: '#2563EB',
  sci_113: '#93C5FD',
  pfv: '#1D4ED8',
  bi_ltd: '#8B5CF6',
  bi_sas: '#F43F5E',
  bi_uk: '#06B6D4',
  bh: '#6366F1',
  fan_zhang: '#F59E0B',
  fan_zhang_piotr_kacki: '#F59E0B',
  piotr_kacki: '#EAB308',
};

// Jurisdiction-based colors — consistent within each country group
const jurisdictionColorMap = {
  UK: '#2563EB',
  France: '#DC2626',
  'UK/France': '#F59E0B',
};

// Legacy entityConfig keyed by type — used only for backward compat in charts
const entityConfig = {
  br_ltd: { name: 'BR Ltd', fullName: 'Bonjour Residences', color: '#10B981', country: 'UK', currency: 'GBP' },
  eci: { name: 'ECI', fullName: 'Edinburgh Capital Investments', color: '#3B82F6', country: 'France', currency: 'EUR' },
  bi_ltd: { name: 'BI Ltd', fullName: 'Bonjour Investments Ltd', color: '#8B5CF6', country: 'UK', currency: 'GBP' },
  bi_sas: { name: 'BI SAS', fullName: 'Bonjour Investments SAS', color: '#F43F5E', country: 'France', currency: 'EUR' },
  bi_uk: { name: 'BI UK', fullName: 'Bonjour Invest UK', color: '#06B6D4', country: 'UK', currency: 'GBP' },
  bh: { name: 'BH', fullName: 'Bonjour Holding', color: '#6366F1', country: 'UK', currency: 'GBP' },
  fan_zhang: { name: 'Fan Zhang', fullName: 'Fan Zhang', color: '#F59E0B', country: 'UK/FR', currency: 'GBP' },
  fan_zhang_piotr_kacki: { name: 'FZ & PK', fullName: 'Fan Zhang and Piotr Kacki', color: '#F59E0B', country: 'UK/FR', currency: 'EUR' },
  piotr_kacki: { name: 'Piotr Kacki', fullName: 'Piotr Kacki', color: '#EAB308', country: 'UK/FR', currency: 'GBP' },
};

// Alias for backward-compat in inline usages
const countryConfigBase = JURISDICTION_CONFIG;

// Inner component props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
function StatCard(props) {
  const {
    title, value, valueTitle, change, icon: Icon, trend, className,
    subStats, subLine, subLineTitle, onClick,
  } = props;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={cn('hover:shadow-lg transition-all duration-300', onClick && 'cursor-pointer', className)}
        onClick={onClick}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              {/* Compact card figures (formatCompact) pair with valueTitle /
                  subLineTitle / sub.title -- the full-precision
                  (formatCurrencyFull) string as the native `title` attribute,
                  so hovering reveals the exact figure. See lib/chartFormatters.js. */}
              <p className="text-2xl font-bold tracking-tight" title={valueTitle}>{value}</p>
              {change !== undefined && (
                <div className="flex items-center gap-1">
                  {trend === 'up' ? (
                    <TrendingUp className="h-3 w-3 text-green-500" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-red-500" />
                  )}
                  <span className={cn('text-xs font-medium', trend === 'up' ? 'text-green-500' : 'text-red-500')}>
                    {change}
                  </span>
                </div>
              )}
            </div>
            <div className="p-3 rounded-xl bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          </div>
          {subStats && subStats.length > 0 && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              {subStats.map((sub) => (
                <span key={sub.label} className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <span>{sub.flag}</span>
                  <span className="font-medium font-mono" title={sub.title}>{sub.value}</span>
                </span>
              ))}
            </div>
          )}
          {subLine && (
            <p className="text-[11px] text-muted-foreground mt-1 leading-tight" title={subLineTitle}>{subLine}</p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function CashflowChart(props) {
  const { data, currency = 'GBP' } = props;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
            <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          stroke="hsl(var(--muted-foreground))"
          fontSize={12}
          tickFormatter={(value) => String(value).slice(5)}
        />
        <YAxis
          stroke="hsl(var(--muted-foreground))"
          fontSize={12}
          tickFormatter={(value) => `${CURRENCY_SYMBOLS[currency] || currency}${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
          }}
          labelStyle={{ color: 'hsl(var(--foreground))' }}
          formatter={(value) => [formatCurrency(value, currency), '']}
        />
        <Area
          type="monotone"
          dataKey="inflow"
          stroke="hsl(var(--primary))"
          fill="url(#inflowGradient)"
          strokeWidth={2}
          name="Inflow"
        />
        <Area
          type="monotone"
          dataKey="outflow"
          stroke="hsl(var(--accent))"
          fill="url(#outflowGradient)"
          strokeWidth={2}
          name="Outflow"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function AlertsList(props) {
  const { alerts } = props;
  const priorityColors = {
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <div className="space-y-3">
      {alerts.slice(0, 5).map((alert, index) => (
        <motion.div
          key={alert.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className={cn(
            'p-4 rounded-lg border transition-all hover:shadow-md cursor-pointer',
            !alert.is_read && 'border-primary/50 bg-primary/5'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={priorityColors[alert.priority]} variant="secondary">
                  {alert.priority}
                </Badge>
                {!alert.is_read && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
              </div>
              <p className="font-medium text-sm truncate">{alert.title}</p>
              <p className="text-xs text-muted-foreground truncate mt-1">{alert.message}</p>
            </div>
          </div>
        </motion.div>
      ))}
      {alerts.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No alerts</p>
        </div>
      )}
    </div>
  );
}


// BankGroup removed: dashboard now uses SharedBankGroup from BankBalanceRows.jsx
// via bankBalancesGrouping.js, eliminating the duplicated inline grouping/formatting
// that caused the string-concatenation bug in per-bank totals.


function EntityComparisonChart(props) {
  const { entityData, allEntities, currency = 'GBP' } = props;
  const entityById = Object.fromEntries((allEntities || []).map((e) => [e.id, e]));
  const chartData = Object.entries(entityData)
    .filter(([id]) => entityById[id])
    .map(([id, data]) => {
      const ent = entityById[id];
      return {
        name: ent.name.length > 20 ? ent.name.slice(0, 18) + '...' : ent.name,
        revenue: data?.financials?.revenue || 0,
        expenses: data?.financials?.expenses || 0,
        balance: data?.bank_balance || 0,
        fill: jurisdictionColorMap[ent.country] || entityTypeColorMap[ent.type] || '#6B7280',
      };
    })
    .filter((d) => d.revenue > 0 || d.expenses > 0);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 80 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis type="number" tickFormatter={(v) => `${CURRENCY_SYMBOLS[currency] || currency}${(v / 1000).toFixed(0)}k`} />
        <YAxis dataKey="name" type="category" width={70} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
          }}
          formatter={(value) => [formatCurrency(value, currency), '']}
        />
        <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[0, 4, 4, 0]}>
          <LabelList dataKey="revenue" position="right" formatter={(v) => formatCompact(v, currency)} style={{ fontSize: 10, fontWeight: 600, fill: '#1f2937' }} />
        </Bar>
        <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[0, 4, 4, 0]}>
          <LabelList dataKey="expenses" position="right" formatter={(v) => formatCompact(v, currency)} style={{ fontSize: 10, fontWeight: 600, fill: '#1f2937' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function BalanceDistributionChart(props) {
  const { entityData, allEntities } = props;
  const entityById = Object.fromEntries((allEntities || []).map((e) => [e.id, e]));
  const pieData = Object.entries(entityData)
    .filter(([id]) => entityById[id])
    .map(([id, data]) => {
      const ent = entityById[id];
      return {
        name: ent.name.length > 20 ? ent.name.slice(0, 18) + '...' : ent.name,
        value: Math.max(0, data?.bank_balance || 0),
        color: jurisdictionColorMap[ent.country] || entityTypeColorMap[ent.type] || '#6B7280',
      };
    })
    .filter(d => d.value > 0);

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          dataKey="value"
        >
          {pieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
          }}
          formatter={(value) => [formatCurrency(value), 'Balance']}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Numeric series keys on entityGrossRevenueChartData/entityMarginChartData
// rows (lib/entityGrossRevenueChart.js) that carry money and must move with
// the display-currency toggle. Non-numeric keys (month, _monthKey, _carrier,
// _isForecast) are left untouched.
const MONEY_CHART_KEYS = ['BR OS', 'BR OS_forecast', 'Portfolio', 'Portfolio_forecast', '_grossMargin', '_netMargin', '_total', '_forecastTotal'];

function convertChartRowsCurrency(rows, fromCurrency, toCurrency, eurGbpRate) {
  if (!rows.length || fromCurrency === toCurrency) return rows;
  return rows.map((row) => {
    const converted = { ...row };
    MONEY_CHART_KEYS.forEach((key) => {
      if (typeof row[key] === 'number') {
        converted[key] = convertAmount(row[key], fromCurrency, toCurrency, eurGbpRate);
      }
    });
    return converted;
  });
}

export function DashboardPage() {
  const { selectedEntity, selectedScope, setSelectedScope, entities, setSelectedEntity } = useEntityStore();
  const { setAlerts, alerts } = useAlertsStore();
  const displayCurrency = usePortfolioPrefsStore(function (s) { return s.displayCurrency; });
  const [searchParams] = useSearchParams();

  // URL param sync (Correction 2): scope is URL-addressable.
  // On mount, the URL param overrides the store.
  const urlScope = searchParams.get('scope');

  useEffect(() => {
    if (urlScope && SCOPES.includes(urlScope) && urlScope !== selectedScope) {
      setSelectedScope(urlScope);
    }
  }, [urlScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPersonalAggregate = selectedScope === 'personal' && selectedEntity === 'all';
  // Legacy group scope: treat as entities aggregate
  useEffect(() => {
    if (urlScope === 'group') {
      setSelectedScope('entities');
      setSelectedEntity('all');
    }
  }, [urlScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const [summary, setSummary] = useState(null);
  const [cashflowData, setCashflowData] = useState([]);
  const [entityData, setEntityData] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankSummaryData, setBankSummaryData] = useState(null);
  const [wealthAssets, setWealthAssets] = useState([]);
  const [fxExposure, setFxExposure] = useState(null);
  const [cashRunway, setCashRunway] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Single-entity dashboard redesign (step 2): the entity's own P&L (BR OS or
  // Property Portfolio, routed by PNL_SOURCE_REGISTRY, classify_booking_pnl
  // for the portfolio case) and its A&L balance-sheet, the same predicate the
  // Assets & Liabilities page renders from. Both null for the aggregate view.
  const [entityPnl, setEntityPnl] = useState(null);
  const [entityBalanceSheet, setEntityBalanceSheet] = useState(null);
  // Part 1b: Net Assets 12m historical series (always GBP, same fixed
  // convention the Net Assets card already uses for this scope).
  const [entityNetAssetsSeries, setEntityNetAssetsSeries] = useState(null);
  // Step 3: forward-booking forecast for the main chart (Property Portfolio
  // entities only; BR OS has no forward-booking source). Fetched
  // unconditionally alongside the other single-entity calls -- cheap, and
  // buildEntityGrossRevenueChartData ignores it for non-portfolio sources.
  const [entityForecast, setEntityForecast] = useState(null);
  // BR OS 3-month forward revenue (contracted nights). BR OS revenue belongs
  // to exactly one entity (config.PNL_SOURCE_REGISTRY: only br_ltd is
  // 'bros'), so this series is applied to that entity and to the merged
  // 'business' aggregate -- counted once either way.
  const [brosForecast, setBrosForecast] = useState(null);
  // Step 4: per-bank monthly balance history (backend/services/
  // bank_account_balance_snapshots.py) and the group cash forecast (for this
  // entity's projected-balance continuation -- no per-bank forecast source
  // exists, see lib/entityBankBalanceHistoryChart.js).
  const [entityBankHistory, setEntityBankHistory] = useState(null);
  const [entityCashForecast, setEntityCashForecast] = useState(null);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);

  // Portfolio mode (scope-driven, Phase 1 shim)
  const isPortfolio = selectedScope === 'portfolio';
  const [portfolioYear, setPortfolioYear] = useState(new Date().getFullYear());
  const [portfolioRegion, setPortfolioRegion] = useState('all');
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioUkData, setPortfolioUkData] = useState(null);
  const [portfolioFrData, setPortfolioFrData] = useState(null);

  // Forecast: rolling 90-day forward bookings for portfolio mode
  const { forecastByMonth } = useForecast({
    scope: 'portfolio', region: portfolioRegion, year: portfolioYear,
  });
  const hasForecast = isPortfolio && forecastByMonth && Object.keys(forecastByMonth).length > 0;

  const selectedEntityObj = selectedEntity !== 'all' && !isPortfolio
    ? entities.find((e) => e.id === selectedEntity)
    : null;
  const currency = selectedEntityObj?.currency || 'GBP';
  // Live GBP/EUR rate for the display-currency toggle (backend/services/
  // entity_pnl.py's fx_rates_used), independent of `currency`/entityPnl's own
  // native reporting currency -- see convertAmount's usage below.
  const eurGbpRate = parseFloat(entityPnl?.fx_rates_used?.EUR_GBP) || 0;
  const isAggregateView = selectedEntity === 'all';
  // 'entities' (All Entities, business-only) or 'personal' (All Personal),
  // null for the single-entity/portfolio/overall cases. Explicitly
  // whitelisted rather than "isAggregateView ? selectedScope : null":
  // selectedEntity defaults to 'all' with selectedScope 'overall' on first
  // load (the pre-redirect-to-/overall render), and unlike the older
  // dashboard.py-backed calls below (which silently no-op on an
  // unrecognized scope), these endpoints 400 on anything other than
  // 'personal'/'entities' when entity_id is absent -- so an unfiltered
  // pass-through here would fire needless failing requests on every login.
  // Drives the aggregate-scope fetches below (get_business_pnl/
  // get_personal_pnl, the A&L aggregate balance sheet, the aggregate
  // net-assets series and bank-balance history) -- the SAME canonical
  // predicates the single-entity view already uses, never a parallel
  // computation.
  const aggregateScopeParam = isAggregateView && (selectedScope === 'entities' || selectedScope === 'personal')
    ? selectedScope
    : null;

  // Bank Balance stat card sublines: grouped by bank/institution, not by
  // entity, at every scope (aggregate or single-entity) -- reuses the same
  // groupAccountsByBank grouping the Bank Accounts card below renders from,
  // so the headline (sum of these groups) is guaranteed to reconcile to the
  // sublines by construction, and to the Bank Accounts card's own total row.
  // Previously the aggregate view grouped by entity instead
  // (groupAccountsByEntityThenBank); replaced so the breakdown answers "which
  // bank" the same way at every scope, per-bank institution identity (with
  // BankLogo's own graceful initials fallback) being the more actionable cut
  // than "which entity" once a per-bank list is available everywhere.
  // Declared here (before any early return below) so hook order stays fixed
  // across renders, per the Rules of Hooks.
  // fx_rates_spot, not fx_rates: fx_rates is money_to_str-quantized to 2dp
  // for backward compatibility (see routes/bank_accounts.py), a ~0.3%+ error
  // versus the unrounded rate the live bank-balance predicate/chart use --
  // exactly the kind of FX-vintage drift that desyncs this card from its
  // siblings. fx_rates_spot carries the same get_rate_safe() rate losslessly.
  const bankBalanceGroups = useMemo(() => {
    if (!bankSummaryData?.accounts) return [];
    return groupAccountsByBank(bankSummaryData.accounts, displayCurrency, bankSummaryData.fx_rates_spot || {});
  }, [bankSummaryData, displayCurrency]);
  const bankBalanceHeadline = bankBalanceGroups.reduce((sum, g) => sum + g.subtotal, 0);

  // Aggregate-only: Property Portfolio / BR OS split subline for the
  // Gross/Net Revenue cards (a single entity is always exactly one source,
  // so this split only makes sense for the mixed 'business' aggregate; for
  // 'personal' every month's BR OS side is genuinely zero, so it's simply
  // omitted here rather than shown as a fabricated "BR OS 0" line).
  // Actuals-scoped to match grossRevenueCard.headline's own basis
  // (computeGrossRevenueCard / _grossRevenueActualTotal in
  // lib/entityStatCards.js): BR OS's revenue is always fully actual, but
  // Portfolio's gross_revenue is a full-year total that includes forward
  // bookings -- summing that unconditionally overstated this subline well
  // past the headline it's supposed to explain (caught live: BR OS
  // 970,529 + Portfolio 776,693 shown against a 1,437,733 headline).
  const aggregateRevenueSplit = useMemo(() => {
    if (!isAggregateView || !entityPnl?.months) return null;
    const brOs = entityPnl.months.reduce((sum, m) => sum + brosRevenueActual(m), 0);
    const portfolio = entityPnl.months.reduce((sum, m) => {
      const actual = m.gross_revenue_actual;
      return sum + (actual != null ? (parseFloat(actual) || 0) : (parseFloat(m.gross_revenue) || 0));
    }, 0);
    if (brOs === 0 && portfolio === 0) return null;
    const nativeCur = entityPnl.currency || 'GBP';
    const brOsConverted = convertAmount(brOs, nativeCur, displayCurrency, eurGbpRate);
    const portfolioConverted = convertAmount(portfolio, nativeCur, displayCurrency, eurGbpRate);
    // {text, title}: compact display paired with the full-precision string
    // for the card's hover tooltip, same convention as the headline value.
    const textParts = [];
    const titleParts = [];
    if (brOs > 0) {
      textParts.push(`BR OS ${formatCompact(brOsConverted, displayCurrency)}`);
      titleParts.push(`BR OS ${formatCurrencyFull(brOsConverted, displayCurrency)}`);
    }
    if (portfolio > 0) {
      textParts.push(`Portfolio ${formatCompact(portfolioConverted, displayCurrency)}`);
      titleParts.push(`Portfolio ${formatCurrencyFull(portfolioConverted, displayCurrency)}`);
    }
    return { text: textParts.join(' · '), title: titleParts.join(' · ') };
  }, [isAggregateView, entityPnl, displayCurrency, eurGbpRate]);
  const grossRevenueCard = useMemo(() => computeGrossRevenueCard(entityPnl), [entityPnl]);
  const netAssetsCard = useMemo(() => computeNetAssetsCard(entityBalanceSheet), [entityBalanceSheet]);
  // Part 3 (dashboards round 2): full stat-card set mirroring the group
  // dashboard, same underlying entity-pnl/balance-sheet sources as above.
  const netRevenueCard = useMemo(() => computeNetRevenueCard(entityPnl), [entityPnl]);
  const marginCards = useMemo(() => computeMarginCards(entityPnl), [entityPnl]);
  const propertiesCard = useMemo(() => computePropertiesCard(entityPnl), [entityPnl]);

  // Net Revenue by component (BR OS / Portfolio): same gross-revenue-minus-
  // ota-commission basis computeNetRevenueCard blends, split by source
  // instead. Aggregate-only, same reasoning as aggregateRevenueSplit above
  // (a single entity is always exactly one source).
  const netRevenueSplit = useMemo(() => {
    if (!isAggregateView || !entityPnl?.months) return null;
    const months = entityPnl.months;
    // BR OS net revenue = turnover less the supplier payment (COGS), same as the card.
    const brOs = months.reduce((sum, m) => sum + brosRevenueActual(m) - brosCogsActual(m), 0);
    const portfolioGross = months.reduce((sum, m) => {
      const actual = m.gross_revenue_actual;
      return sum + (actual != null ? (parseFloat(actual) || 0) : (parseFloat(m.gross_revenue) || 0));
    }, 0);
    const portfolioOta = months.reduce((sum, m) => {
      const actual = m.ota_commission_actual;
      return sum + (actual != null ? (parseFloat(actual) || 0) : (parseFloat(m.ota_commission) || 0));
    }, 0);
    const portfolio = portfolioGross - portfolioOta;
    if (brOs === 0 && portfolio === 0) return null;
    const nativeCur = entityPnl.currency || 'GBP';
    const brOsConverted = convertAmount(brOs, nativeCur, displayCurrency, eurGbpRate);
    const portfolioConverted = convertAmount(portfolio, nativeCur, displayCurrency, eurGbpRate);
    const textParts = []; const titleParts = [];
    if (brOs > 0) { textParts.push(`BR OS ${formatCompact(brOsConverted, displayCurrency)}`); titleParts.push(`BR OS ${formatCurrencyFull(brOsConverted, displayCurrency)}`); }
    if (portfolio > 0) { textParts.push(`Portfolio ${formatCompact(portfolioConverted, displayCurrency)}`); titleParts.push(`Portfolio ${formatCurrencyFull(portfolioConverted, displayCurrency)}`); }
    return { text: textParts.join(' · '), title: titleParts.join(' · ') };
  }, [isAggregateView, entityPnl, displayCurrency, eurGbpRate]);

  // Gross/Net Margin % by component (BR OS / Portfolio): reuses the exact
  // additive split get_business_pnl already computes for the MarginChart
  // (bros_net_margin_actual/portfolio_net_margin_actual/bros_gross_margin_actual/
  // portfolio_gross_margin_actual, entity_pnl.py) -- summed across months
  // here instead of read per-month by the chart, never re-derived. Percent
  // is against that component's OWN actual revenue (same basis
  // netRevenueSplit's brOs/portfolioGross use), not the blended headline
  // revenue, so "BR OS 61.2%" is BR OS's own margin rate. Aggregate-only,
  // same reasoning as netRevenueSplit above.
  const marginComponentSplit = useMemo(() => {
    if (!isAggregateView || !entityPnl?.months) return null;
    const months = entityPnl.months;
    const sumField = (key) => months.reduce((sum, m) => sum + (parseFloat(m[key]) || 0), 0);
    const brOsRevenue = months.reduce((sum, m) => sum + brosRevenueActual(m), 0);
    const portfolioRevenue = months.reduce((sum, m) => {
      const actual = m.gross_revenue_actual;
      return sum + (actual != null ? (parseFloat(actual) || 0) : (parseFloat(m.gross_revenue) || 0));
    }, 0);
    const brOsNet = sumField('bros_net_margin_actual');
    const brOsGross = sumField('bros_gross_margin_actual');
    // A portfolio-only aggregate (All Personal) has no per-source split
    // fields: its whole net/gross margin IS the Portfolio component. Reading
    // the absent split fields showed "Portfolio 0.0%" (Fan, 2026-10-25).
    const hasSplit = months.some((m) => m.portfolio_net_margin_actual != null);
    const portfolioNet = hasSplit ? sumField('portfolio_net_margin_actual') : sumField('net_result_actual');
    const portfolioGross = hasSplit ? sumField('portfolio_gross_margin_actual') : sumField('gross_margin_actual');
    const hasBrOs = brOsRevenue > 0 || brOsNet !== 0;
    const hasPortfolio = portfolioRevenue > 0 || portfolioNet !== 0;
    if (!hasBrOs && !hasPortfolio) return null;
    const pct = (amount, revenue) => (revenue !== 0 ? (amount / revenue) * 100 : 0);
    const netParts = []; const grossParts = [];
    if (hasBrOs) {
      netParts.push(`BR OS ${pct(brOsNet, brOsRevenue).toFixed(1)}%`);
      grossParts.push(`BR OS ${pct(brOsGross, brOsRevenue).toFixed(1)}%`);
    }
    if (hasPortfolio) {
      netParts.push(`Portfolio ${pct(portfolioNet, portfolioRevenue).toFixed(1)}%`);
      grossParts.push(`Portfolio ${pct(portfolioGross, portfolioRevenue).toFixed(1)}%`);
    }
    return { netText: netParts.join(' · '), grossText: grossParts.join(' · ') };
  }, [isAggregateView, entityPnl]);

  // Personal holds no BR OS business: drop the empty BR OS series from the
  // booking-volume and margin charts there (Fan, 2026-10-25).
  const revenueSegments = selectedScope === 'personal'
    ? ENTITY_GROSS_REVENUE_SEGMENTS.filter((seg) => seg.key !== 'br_os')
    : ENTITY_GROSS_REVENUE_SEGMENTS;

  // Properties by country (France / UK): mirrors PropertyPortfolioPage's
  // PortfolioKpiCards regionSub pattern. entityPnl.property_count_by_region
  // (services/entity_pnl.py, derived from build_canonical_properties' region
  // field via properties_meta, never re-derived here). Not a money figure,
  // so no convertAmount pass. Shown at any scope (aggregate or single-entity)
  // -- a single entity can genuinely own properties in both regions.
  const propertiesCountrySplit = useMemo(() => {
    const byRegion = entityPnl?.property_count_by_region;
    if (!byRegion) return null;
    const uk = byRegion.uk || 0;
    const fr = byRegion.fr || 0;
    if (uk === 0 && fr === 0) return null;
    const parts = [];
    if (fr > 0) parts.push(`France ${fr}`);
    if (uk > 0) parts.push(`UK ${uk}`);
    return parts.join(' · ');
  }, [entityPnl]);
  // Step 3: main chart data. entityPnl.source is 'bros' | 'portfolio' | 'none'
  // (PNL_SOURCE_REGISTRY) -- exactly one of BR OS/Portfolio is ever nonzero
  // for a given entity, so this degrades gracefully to a single segment (or
  // an all-zero chart for a holding entity).
  const entityGrossRevenueChartData = useMemo(() => {
    if (!entityPnl) return [];
    const rows = buildEntityGrossRevenueChartData({
      year: new Date().getFullYear(),
      source: entityPnl.source,
      months: entityPnl.months,
      forecastMonthly: entityForecast?.monthly,
      brosForecastMonths: brosForecast,
      // The forecast's gross_revenue_gbp is always GBP; convert to this
      // entity's own currency first (so a EUR-native entity's forecast bar
      // isn't a raw GBP number mislabeled as EUR) -- the display-currency
      // toggle conversion below is a separate, final pass over the whole
      // built series, same convertAmount call, just to a different target.
      entityCurrency: entityPnl.currency,
      fxRates: bankSummaryData?.fx_rates,
      monthLabels: MONTH_NAMES_SHORT,
    });
    return convertChartRowsCurrency(rows, entityPnl.currency || currency, displayCurrency, eurGbpRate);
  }, [entityPnl, entityForecast, brosForecast, bankSummaryData, currency, displayCurrency, eurGbpRate]);

  // Margin chart data (companion to entityGrossRevenueChartData above, now
  // that the Gross Revenue chart no longer carries margin lines): same
  // inputs, same BR OS/Portfolio segment split and degrade rule, same
  // forecast source.
  const entityMarginChartData = useMemo(() => {
    if (!entityPnl) return [];
    const rows = buildEntityMarginChartData({
      year: new Date().getFullYear(),
      source: entityPnl.source,
      months: entityPnl.months,
      forecastMonthly: entityForecast?.monthly,
      brosForecastMonths: brosForecast,
      entityCurrency: entityPnl.currency,
      fxRates: bankSummaryData?.fx_rates,
      monthLabels: MONTH_NAMES_SHORT,
    });
    return convertChartRowsCurrency(rows, entityPnl.currency || currency, displayCurrency, eurGbpRate);
  }, [entityPnl, entityForecast, brosForecast, bankSummaryData, currency, displayCurrency, eurGbpRate]);

  // Step 4: bank balance history chart data (dashboards round 3): calendar-
  // year Jan-Dec, no forecast (see lib/entityBankBalanceHistoryChart.js).
  // The entity's own forecast (entityCashForecast) still feeds the Net
  // Assets chart's forecast continuation directly, below -- unaffected by
  // this chart dropping its forecast segment.
  const bankBalanceHistoryChartData = useMemo(() => {
    if (!entityBankHistory) return { rows: [], series: [] };
    return buildBankBalanceHistoryChartData({
      monthsData: entityBankHistory.months,
      year: new Date().getFullYear(),
    });
  }, [entityBankHistory]);

  // Monthly Cashflow chart data (dashboards round 3): bars are
  // cashRunway.calendar_year_months (Jan-Dec, the same figures the "Avg Net
  // Cashflow (YTD, complete months)" card averages, minus the current month
  // -- see computeAvgNetCashflow's own comment), net margin line from the
  // Gross Revenue chart's own rows. entityGrossRevenueChartData is already
  // display-currency-converted (see its own memo above); calendar_year_months
  // comes straight from the cash-runway endpoint in the entity's native
  // currency, so it needs the same conversion here or the two series would
  // mix currencies on one chart.
  const monthlyCashflowChartData = useMemo(() => {
    const nativeCur = entityPnl?.currency || currency;
    const convertedMonths = (cashRunway?.calendar_year_months || []).map((m) => (
      m.net == null ? m : { ...m, net: convertAmount(parseFloat(m.net) || 0, nativeCur, displayCurrency, eurGbpRate) }
    ));
    return buildMonthlyCashflowChartData({
      calendarYearMonths: convertedMonths,
      netMarginRows: entityGrossRevenueChartData,
    });
  }, [cashRunway, entityGrossRevenueChartData, entityPnl, currency, displayCurrency, eurGbpRate]);

  // Avg Net Cashflow by component (BR OS / Portfolio): same calendar-year
  // complete-months average computeAvgNetCashflow uses for the headline,
  // applied per scope to cashRunway.calendar_year_months_by_scope
  // (routes/dashboard.py's get_cash_runway -- same raw-transaction basis as
  // calendar_year_months, split by the canonical _ENTITY_TO_SCOPE map, added
  // specifically because this card averages the calendar-year series, not
  // the trailing-6-month one avg_monthly_net_by_scope already split).
  // Fallback (zero complete months, e.g. January) mirrors the headline's own
  // fallback to the trailing-6-month figure, just per scope
  // (avg_monthly_net_by_scope, present unconditionally). A scope key absent
  // from calendar_year_months_by_scope means zero transactions for that
  // component all year -- genuinely absent, not a zero to render.
  // Aggregate-only: a single entity's cash movement is already one component.
  const cashflowComponentSplit = useMemo(() => {
    if (!isAggregateView || !cashRunway?.calendar_year_months_by_scope) return null;
    const nativeCur = entityPnl?.currency || currency;
    const byScope = cashRunway.calendar_year_months_by_scope;
    const brOsRows = byScope.br_os;
    const portfolioRows = byScope.property_portfolio;
    if (!brOsRows && !portfolioRows) return null;
    const avgForScope = (rows, fallbackNative) => {
      const converted = rows.map((m) => (
        m.net == null ? m : { ...m, net: convertAmount(parseFloat(m.net) || 0, nativeCur, displayCurrency, eurGbpRate) }
      ));
      const fallback = convertAmount(parseFloat(fallbackNative || 0), nativeCur, displayCurrency, eurGbpRate);
      return computeAvgNetCashflow(buildMonthlyCashflowChartData({ calendarYearMonths: converted }), fallback);
    };
    const textParts = []; const titleParts = [];
    if (brOsRows) {
      const avg = avgForScope(brOsRows, cashRunway.avg_monthly_net_by_scope?.br_os);
      textParts.push(`BR OS ${formatCompact(avg, displayCurrency)}`);
      titleParts.push(`BR OS ${formatCurrencyFull(avg, displayCurrency)}`);
    }
    if (portfolioRows) {
      const avg = avgForScope(portfolioRows, cashRunway.avg_monthly_net_by_scope?.property_portfolio);
      textParts.push(`Portfolio ${formatCompact(avg, displayCurrency)}`);
      titleParts.push(`Portfolio ${formatCurrencyFull(avg, displayCurrency)}`);
    }
    return { text: textParts.join(' · '), title: titleParts.join(' · ') };
  }, [isAggregateView, cashRunway, entityPnl, currency, displayCurrency, eurGbpRate]);

  // Net Assets 12m chart data (part 1b): always GBP, same fixed convention
  // the Net Assets card uses for this scope. currentCash comes from
  // cashRunway.cash_now (already GBP-converted via the canonical bank-balance
  // predicate, see routes/dashboard.py) rather than bankBalanceHeadline
  // (which is displayCurrency-converted). Forecast continuation converts the
  // entity's own native-currency projected_balance (the same forward-booking
  // floor the Bank Balance chart uses) to GBP via the live spot rates already
  // fetched for that chart -- never a new derivation.
  const netAssetsChartData = useMemo(() => {
    const series = entityNetAssetsSeries?.series || [];
    const currentCashGbp = parseFloat(cashRunway?.cash_now || 0);
    const entityForecastMonthly = entityCashForecast?.entities?.[selectedEntityObj?.type]?.monthly || [];
    const cashForecastRows = entityForecastMonthly.map((m) => ({
      month: monthKeyToLabel(m.month),
      _monthKey: m.month,
      _isForecast: true,
      _forecastTotal: convertAmount(
        parseFloat(m.projected_balance || 0), selectedEntityObj?.currency || 'GBP', 'GBP', bankSummaryData?.fx_rates || {},
      ) || 0,
    }));
    return buildNetAssetsChartData({ series, currentCash: currentCashGbp, cashForecastRows });
  }, [entityNetAssetsSeries, cashRunway, entityCashForecast, selectedEntityObj, bankSummaryData]);

  const fetchData = async () => {
    try {
      if (isPortfolio) {
        if (portfolioRegion === 'all') {
          const [allResult, ukResult, frResult] = await Promise.all([
            portfolioAPI.get({ region: 'all', year: portfolioYear }),
            portfolioAPI.get({ region: 'uk', year: portfolioYear }),
            portfolioAPI.get({ region: 'fr', year: portfolioYear }),
          ]);
          setPortfolioData(allResult.data);
          setPortfolioUkData(ukResult.data);
          setPortfolioFrData(frResult.data);
        } else {
          const result = await portfolioAPI.get({ region: portfolioRegion, year: portfolioYear });
          setPortfolioData(result.data);
          setPortfolioUkData(null);
          setPortfolioFrData(null);
        }
        setSummary(null);
        setCashflowData([]);
        setBankAccounts([]);
        setBankSummaryData(null);
        setEntityData({});
        setEntityPnl(null);
        setEntityBalanceSheet(null);
        setEntityForecast(null);
        setEntityBankHistory(null);
        setEntityCashForecast(null);
      } else {
        // Single-entity view, or an aggregate (All Entities / All Personal):
        // fetch the detailed dashboard data, scoped by selectedScope. The
        // aggregate cases pass entity_id=null and let scope decide which
        // entities are summed (entities = business only, personal = personal
        // only), so All Entities excludes personal and vice versa.
        setPortfolioData(null);
        setPortfolioUkData(null);
        setPortfolioFrData(null);
        const entityId = isAggregateView ? null : (selectedEntityObj?.id || null);
        const scopeParam = { scope: selectedScope };

        const [summaryRes, cashflowRes, alertsRes, accountsRes, wealthAssetsRes, fxExpRes, runwayRes, bankSummaryRes, entityPnlRes, balanceSheetRes, entityForecastRes, cashForecastRes, netAssetsSeriesRes] = await Promise.allSettled([
          dashboardAPI.getSummary(entityId, scopeParam),
          dashboardAPI.getCashflow(entityId, 30, scopeParam),
          alertsAPI.getAll({ entity_id: entityId, is_read: false }),
          bankAccountsAPI.getAll(entityId),
          wealthAPI.getAssets(),
          dashboardAPI.getFxExposure(entityId, scopeParam),
          dashboardAPI.getCashRunway(entityId, { ...scopeParam, calendarYear: true }),
          bankAccountsAPI.getSummary({ entity_id: entityId, scope: isAggregateView ? selectedScope : undefined }),
          // Single-entity dashboard redesign (steps 2-4), extended to the All
          // Entities / All Personal aggregates: entityId is null there, so
          // these fall back to the scope-level aggregate predicate instead
          // (get_business_pnl/get_personal_pnl, the aggregate A&L balance
          // sheet, the aggregate net-assets series) -- the same canonical
          // sources the single-entity view uses, just summed across the
          // scope's members server-side. Forward-booking forecast and the
          // group cash forecast stay entity-only: there is no aggregate
          // forward-booking source, and the group cash forecast is already
          // per-entity so an aggregate continuation isn't derivable from a
          // single call here. Bank balance history is fetched by its own
          // effect below (it's the only one of these calls parameterized by
          // displayCurrency server-side, so it needs to react to the
          // currency toggle without forcing every other call here to refetch
          // too).
          entityId
            ? reportsAPI.getEntityPnl({ entity_id: entityId, year: new Date().getFullYear() })
            : aggregateScopeParam
              ? reportsAPI.getEntityPnl({ scope: aggregateScopeParam, year: new Date().getFullYear() })
              : Promise.resolve({ data: null }),
          entityId
            ? assetsLiabilitiesAPI.getBalanceSheet('entity', entityId, 'GBP')
            : aggregateScopeParam
              ? assetsLiabilitiesAPI.getBalanceSheet(aggregateScopeParam === 'personal' ? 'personal' : 'entity', null, 'GBP')
              : Promise.resolve({ data: null }),
          entityId ? forecastAPI.get({ scope: 'entity', id: entityId }) : Promise.resolve({ data: null }),
          entityId ? groupForecastAPI.get() : Promise.resolve({ data: null }),
          // Net Assets chart (dashboards round 3): calendar-year Jan-Dec
          // series (always GBP).
          entityId
            ? assetsLiabilitiesAPI.getNetAssetsSeries('entity', entityId, 'GBP', 24, true)
            : aggregateScopeParam
              ? assetsLiabilitiesAPI.getNetAssetsSeries(aggregateScopeParam === 'personal' ? 'personal' : 'entity', null, 'GBP', 24, true)
              : Promise.resolve({ data: null }),
        ]);

        if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
        if (cashflowRes.status === 'fulfilled') setCashflowData(cashflowRes.value.data);
        if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data);
        if (accountsRes.status === 'fulfilled') setBankAccounts(accountsRes.value.data);
        if (wealthAssetsRes.status === 'fulfilled') setWealthAssets(wealthAssetsRes.value.data || []);
        if (fxExpRes.status === 'fulfilled') setFxExposure(fxExpRes.value.data);
        if (runwayRes.status === 'fulfilled') setCashRunway(runwayRes.value.data);
        if (bankSummaryRes.status === 'fulfilled') setBankSummaryData(bankSummaryRes.value.data);
        setEntityPnl(entityPnlRes.status === 'fulfilled' ? entityPnlRes.value.data : null);
        setEntityBalanceSheet(balanceSheetRes.status === 'fulfilled' ? balanceSheetRes.value.data : null);
        setEntityForecast(entityForecastRes.status === 'fulfilled' ? entityForecastRes.value.data : null);
        // BR OS forward months: only for views that actually carry BR OS
        // revenue, so no other entity's chart can ever paint them.
        var pnlSource = entityPnlRes.status === 'fulfilled' ? (entityPnlRes.value.data || {}).source : null;
        if (pnlSource === 'bros' || pnlSource === 'business') {
          reportsAPI.brosDashboard('ytd')
            .then((res) => setBrosForecast(res.data.forecast_months || []))
            .catch(() => setBrosForecast(null));
        } else {
          setBrosForecast(null);
        }
        setEntityCashForecast(cashForecastRes.status === 'fulfilled' ? cashForecastRes.value.data : null);
        setEntityNetAssetsSeries(netAssetsSeriesRes.status === 'fulfilled' ? netAssetsSeriesRes.value.data : null);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || isPortfolio || isAggregateView) {
      setIsLoading(true);
      fetchData();
    }
  }, [selectedEntity, selectedScope, entities, portfolioYear, portfolioRegion]);

  // Bank balance history (step 4) is fetched separately from the main
  // fetchData batch above: it's the only single-entity call whose response
  // is converted to displayCurrency server-side (every other figure on this
  // page converts client-side from native amounts, so it doesn't need a
  // network refetch when the currency toggle changes). This effect keeps
  // that one call in sync with the toggle without forcing every other call
  // in fetchData to refetch too.
  useEffect(() => {
    const entityId = isAggregateView || isPortfolio ? null : (selectedEntityObj?.id || null);
    const scope = isPortfolio ? null : aggregateScopeParam;
    if (!entityId && !scope) {
      setEntityBankHistory(null);
      return;
    }
    let cancelled = false;
    // months=12 trailing always reaches back to January 1 of the current
    // calendar year regardless of today's month (at most 11 months separate
    // "now" from this year's January), which is all the calendar-year chart
    // (round 3) needs -- buildBankBalanceHistoryChartData discards anything
    // outside the current Jan-Dec window itself.
    bankBalanceHistoryAPI.get(entityId, { months: 12, displayCurrency, scope })
      .then((res) => { if (!cancelled) setEntityBankHistory(res.data); })
      .catch(() => { if (!cancelled) setEntityBankHistory(null); });
    return () => { cancelled = true; };
  }, [isAggregateView, isPortfolio, selectedEntityObj, displayCurrency, aggregateScopeParam]);

  // Reset region tab when leaving portfolio mode
  useEffect(() => {
    if (!isPortfolio) setPortfolioRegion('all');
  }, [isPortfolio]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    toast.success('Dashboard refreshed');
  };


  const handleEntitySelect = (entityTypeOrId) => {
    // If it's an entity type, find the first entity of that type
    const isType = ['br_ltd', 'hsr', 'eci', 'afpv', 'bi_immo', 'sci_113', 'pfv', 'bi_ltd', 'bi_sas', 'bi_uk', 'bh', 'fan_zhang', 'fan_zhang_piotr_kacki', 'piotr_kacki'].includes(entityTypeOrId);
    if (isType) {
      const ent = entities.find((e) => e.type === entityTypeOrId);
      if (ent) setSelectedEntity(ent.id);
    } else {
      setSelectedEntity(entityTypeOrId);
    }
  };

  const entityById = Object.fromEntries(entities.map((e) => [e.id, e]));
  // Fallback lookup: entity type string → first entity of that type (handles
  // bank accounts created before the Wise sync was fixed to use real UUIDs).
  const entityByType = {};
  for (const e of entities) {
    if (!entityByType[e.type]) entityByType[e.type] = e;
  }

  // Group bank accounts: jurisdiction > entity > accounts
  const { balByJurisdiction, balByEntity } = useMemo(() => {
    const jurs = {};
    const ents = {};
    for (const account of bankAccounts) {
      const entity = entityById[account.entity_id] || entityByType[account.entity_id];
      if (!entity) continue;
      const cur = account.currency || 'GBP';
      // null/undefined balance means "feed never reported", not zero money --
      // the account still counts toward its entity's accounts list, it just
      // contributes nothing to the balance sum.
      const bal = account.balance;
      const hasBal = bal != null;
      if (!ents[entity.id]) ents[entity.id] = { entity, balances: {}, accounts: [] };
      if (hasBal) ents[entity.id].balances[cur] = (ents[entity.id].balances[cur] || 0) + bal;
      ents[entity.id].accounts.push(account);
      // Find jurisdiction for this entity
      const jurKey = Object.entries(countryConfigBase).find(
        ([, cfg]) => cfg.entityTypes.includes(entity.type) && cfg.country === entity.country
      )?.[0];
      if (!jurKey) continue;
      if (!jurs[jurKey]) jurs[jurKey] = { balances: {} };
      if (hasBal) jurs[jurKey].balances[cur] = (jurs[jurKey].balances[cur] || 0) + bal;
    }
    return { balByJurisdiction: jurs, balByEntity: ents };
  }, [bankAccounts, entityById]);

  // Total bank balance across all accounts, properly converted to GBP.
  // A null balance is excluded from the sum, never coerced to 0.
  const totalBankBalanceGBP = useMemo(() => {
    return bankAccounts.reduce(
      (sum, acc) => acc.balance == null ? sum : sum + convertCurrency(acc.balance, acc.currency || 'GBP', 'GBP'),
      0
    );
  }, [bankAccounts]);

  // Entity-specific wealth computed from wealthAssets filtered by country
  const entityWealth = useMemo(() => {
    if (!selectedEntityObj || !wealthAssets.length) return null;
    const fx = { EUR: 0.86, USD: 0.79, GBP: 1.0, DKK: 0.11 };
    const entityCountry = selectedEntityObj.country;
    const filtered = wealthAssets.filter((a) => {
      const assetCountry = a.country || ({ EUR: 'France', GBP: 'UK', USD: 'UK', DKK: 'France' }[a.currency] || 'UK');
      return assetCountry === entityCountry;
    });
    let total = 0;
    let realEstate = 0;
    for (const a of filtered) {
      const val = (a.current_value || 0) * (fx[a.currency] || 1);
      total += val;
      if (a.type === 'real_estate') realEstate += val;
    }
    return { total: Math.round(total), realEstate: Math.round(realEstate), other: Math.round(total - realEstate) };
  }, [selectedEntityObj, wealthAssets]);

  // Portfolio revenue chart data (region-aware, matches RealEstatePortfolioPage)
  const portfolioRevenueChartDataBase = useMemo(() => {
    if (!portfolioData?.monthly) return [];
    if (portfolioRegion === 'uk') {
      return portfolioData.monthly.map((m) => {
        const monthIdx = parseInt(m.month.split('-')[1], 10) - 1;
        return { month: MONTH_NAMES_SHORT[monthIdx] || m.month, 'UK (GBP)': m.uk_gbp || 0 };
      });
    }
    if (portfolioRegion === 'fr') {
      return portfolioData.monthly.map((m) => {
        const monthIdx = parseInt(m.month.split('-')[1], 10) - 1;
        return { month: MONTH_NAMES_SHORT[monthIdx] || m.month, 'FR (EUR)': m.fr_eur || 0 };
      });
    }
    // region === 'all': merge per-region monthly series
    if (portfolioUkData?.monthly && portfolioFrData?.monthly) {
      const eurGbpRate = parseFloat(portfolioData?.fx_rates_used?.EUR_GBP) || 0;
      const ukByMonth = {};
      const frByMonth = {};
      for (const m of portfolioUkData.monthly) ukByMonth[m.month] = m;
      for (const m of portfolioFrData.monthly) frByMonth[m.month] = m;
      const allMonths = [...new Set([...Object.keys(ukByMonth), ...Object.keys(frByMonth)])].sort();
      return allMonths.map((monthKey) => {
        const monthIdx = parseInt(monthKey.split('-')[1], 10) - 1;
        const ukGbp = parseFloat(ukByMonth[monthKey]?.uk_gbp) || 0;
        const frEur = parseFloat(frByMonth[monthKey]?.fr_eur) || 0;
        const frGbpApprox = eurGbpRate > 0 ? frEur * eurGbpRate : 0;
        const ukMargin = parseFloat(ukByMonth[monthKey]?.uk_gbp_margin) || 0;
        const frMarginEur = parseFloat(frByMonth[monthKey]?.fr_eur_margin) || 0;
        return {
          month: MONTH_NAMES_SHORT[monthIdx] || monthKey,
          'UK (GBP)': ukGbp,
          'FR (GBP approx)': Math.max(0, frGbpApprox),
          _uk_gbp: ukGbp,
          _fr_eur: frEur,
          _combined: ukGbp + frGbpApprox,
          _uk_gbp_margin: ukMargin,
          _fr_eur_margin: frMarginEur,
          _combined_margin: ukMargin + (eurGbpRate > 0 ? frMarginEur * eurGbpRate : 0),
        };
      });
    }
    // Fallback: combined data
    return portfolioData.monthly.map((m) => {
      const monthIdx = parseInt(m.month.split('-')[1], 10) - 1;
      const frConverted = (m.combined_approx_gbp || 0) - (m.uk_gbp || 0);
      return {
        month: MONTH_NAMES_SHORT[monthIdx] || m.month,
        'UK (GBP)': m.uk_gbp || 0,
        'FR (GBP approx)': Math.max(0, frConverted),
        _uk_gbp: m.uk_gbp,
        _fr_eur: m.fr_eur,
        _combined: m.combined_approx_gbp,
      };
    });
  }, [portfolioData?.monthly, portfolioData?.fx_rates_used, portfolioUkData?.monthly, portfolioFrData?.monthly, portfolioRegion]);

  // Merge forecast into portfolio revenue chart (per-region breakdown from endpoint)
  const portfolioRevenueChartData = useMemo(() => {
    if (portfolioRegion === 'uk') {
      return mergeWithForecast(portfolioRevenueChartDataBase, forecastByMonth, (fm) => ({
        'UK (GBP)': fm.revenue_uk_gbp || fm.revenue_gbp || 0,
      }), portfolioYear);
    }
    if (portfolioRegion === 'fr') {
      return mergeWithForecast(portfolioRevenueChartDataBase, forecastByMonth, (fm) => ({
        'FR (EUR)': fm.revenue_fr_gbp || fm.revenue_gbp || 0,
      }), portfolioYear);
    }
    return mergeWithForecast(portfolioRevenueChartDataBase, forecastByMonth, (fm) => {
      const uk = fm.revenue_uk_gbp || 0;
      const fr = fm.revenue_fr_gbp || 0;
      return { 'UK (GBP)': uk, 'FR (GBP approx)': fr, _combined: uk + fr };
    }, portfolioYear);
  }, [portfolioRevenueChartDataBase, forecastByMonth, portfolioRegion, portfolioYear]);

  // Portfolio avg nightly rate data (All tab only, matches RealEstatePortfolioPage)
  const portfolioAvgNightlyRateDataBase = useMemo(() => {
    if (portfolioRegion !== 'all' || !portfolioUkData?.properties || !portfolioFrData?.properties) return [];
    const eurGbpRate = parseFloat(portfolioData?.fx_rates_used?.EUR_GBP) || 0;
    if (eurGbpRate === 0) return [];

    const computeMonthlyRate = (properties, toGbpRate) => {
      const monthMap = {};
      for (const prop of properties) {
        for (const m of prop.monthly || []) {
          if (!monthMap[m.month]) monthMap[m.month] = { revenue: 0, nights: 0 };
          monthMap[m.month].revenue += (parseFloat(m.revenue_native) || 0) * toGbpRate;
          monthMap[m.month].nights += m.nights || 0;
        }
      }
      return monthMap;
    };

    const ukMonthly = computeMonthlyRate(portfolioUkData.properties, 1);
    const frMonthly = computeMonthlyRate(portfolioFrData.properties, eurGbpRate);
    const allMonths = [...new Set([...Object.keys(ukMonthly), ...Object.keys(frMonthly)])].sort();

    return allMonths.map((monthKey) => {
      const monthIdx = parseInt(monthKey.split('-')[1], 10) - 1;
      const uk = ukMonthly[monthKey];
      const fr = frMonthly[monthKey];
      const totalRev = (uk?.revenue || 0) + (fr?.revenue || 0);
      const totalNights = (uk?.nights || 0) + (fr?.nights || 0);
      return {
        month: MONTH_NAMES_SHORT[monthIdx] || monthKey,
        'Overall': totalNights > 0 ? totalRev / totalNights : null,
        '🇬🇧 UK': uk && uk.nights > 0 ? uk.revenue / uk.nights : null,
        '🇫🇷 France': fr && fr.nights > 0 ? fr.revenue / fr.nights : null,
      };
    });
  }, [portfolioRegion, portfolioUkData?.properties, portfolioFrData?.properties, portfolioData?.fx_rates_used]);

  // Merge forecast into avg nightly rate data (per-region from endpoint)
  const portfolioAvgNightlyRateDataMerged = useMemo(() => {
    return mergeWithForecast(portfolioAvgNightlyRateDataBase, forecastByMonth, (fm) => {
      return {
        'Overall': fm.adr_gbp ?? null,
        '🇬🇧 UK': fm.adr_uk_gbp ?? null,
        '🇫🇷 France': fm.adr_fr_gbp ?? null,
      };
    }, portfolioYear);
  }, [portfolioAvgNightlyRateDataBase, forecastByMonth, portfolioYear]);

  const portfolioAvgNightlyRateData = useMemo(
    () => splitForecastSeries(portfolioAvgNightlyRateDataMerged, ['Overall', '🇬🇧 UK', '🇫🇷 France']),
    [portfolioAvgNightlyRateDataMerged]
  );

  // Refreshes the same canonical balance sheet the Net Assets card renders
  // from (compute_balance_sheet), so an edit here is visible on the card
  // immediately rather than only after the next full dashboard refetch.
  const handleAssetRegisterUpdate = () => {
    if (selectedEntityObj?.id) {
      assetsLiabilitiesAPI.getBalanceSheet('entity', selectedEntityObj.id, 'GBP').then(res => setEntityBalanceSheet(res.data)).catch(() => {});
    } else if (isPersonalAggregate) {
      assetsLiabilitiesAPI.getBalanceSheet('personal', null, 'GBP').then(res => setEntityBalanceSheet(res.data)).catch(() => {});
    } else if (isAggregateView) {
      // "All Entities" business aggregate: compute_balance_sheet's
      // documented convention for scope="entity" with no entity_id.
      assetsLiabilitiesAPI.getBalanceSheet('entity', null, 'GBP').then(res => setEntityBalanceSheet(res.data)).catch(() => {});
    }
  };

  // Overall scope: the group-wide dashboard lives at its own route.
  if (selectedScope === 'overall') {
    return <Navigate to="/overall" replace />;
  }

  // Portfolio scope redirects to the consolidated PropertyPortfolioPage
  if (selectedScope === 'portfolio') {
    return <Navigate to="/portfolio" replace />;
  }

  // BR OS scope redirects to the dedicated BR OS dashboard
  if (selectedScope === 'br_os') {
    return <Navigate to="/bros-dashboard" replace />;
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // ── Portfolio Dashboard ──
  if (isPortfolio && portfolioData) {
    const totals = portfolioData.totals;
    const properties = portfolioData.properties || [];
    const chartCurrency = portfolioRegion === 'fr' ? 'EUR' : 'GBP';

    // Compute KPI values from portfolioAPI.get response
    const totalBookings = properties.reduce((s, p) => s + (p.totals?.bookings || 0), 0);
    const totalNights = properties.reduce((s, p) => s + (p.totals?.nights || 0), 0);
    const totalRevGbp = totals?.combined_approx_gbp ?? (totals?.uk?.revenue || 0);
    const totalMarginGbp = totals?.combined_approx_gbp_margin ?? (totals?.uk?.booking_margin || 0);
    // For margin %, use native-currency values for single-region tabs
    const regionRev = portfolioRegion === 'uk' ? (totals?.uk?.revenue || 0)
      : portfolioRegion === 'fr' ? (totals?.fr?.revenue || 0)
      : totalRevGbp;
    const regionMargin = portfolioRegion === 'uk' ? (totals?.uk?.booking_margin || 0)
      : portfolioRegion === 'fr' ? (totals?.fr?.booking_margin || 0)
      : totalMarginGbp;
    const marginPct = regionRev > 0 ? ((regionMargin / regionRev) * 100).toFixed(1) : 0;

    // Top properties by revenue (sorted from full properties list)
    const topByRevenue = [...properties]
      .sort((a, b) => (b.totals?.revenue_gbp_approx ?? b.totals?.revenue_native ?? 0) - (a.totals?.revenue_gbp_approx ?? a.totals?.revenue_native ?? 0))
      .slice(0, 5);
    const topByMargin = [...properties]
      .filter((p) => p.totals?.margin_pct != null)
      .sort((a, b) => (b.totals?.margin_pct || 0) - (a.totals?.margin_pct || 0))
      .slice(0, 5);

    const regionBadge = (r) => (
      <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', r === 'uk' ? 'border-blue-300 text-blue-600' : 'border-red-300 text-red-600')}>
        {r === 'uk' ? 'UK' : 'FR'}
      </Badge>
    );

    // Stacked chart tooltip for All tab
    const StackedRevenueTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const point = payload[0]?.payload;
      if (!point) return null;
      return (
        <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
          <p className="font-semibold mb-1">{label}</p>
          <div className="flex justify-between gap-4">
            <span className="text-blue-600">UK</span>
            <span className="font-mono">{formatCurrency(point._uk_gbp || 0, 'GBP')}</span>
          </div>
          {point._uk_gbp_margin != null && (
            <div className="flex justify-between gap-4">
              <span className="text-blue-600/60 text-xs ml-2">margin</span>
              <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._uk_gbp_margin, 'GBP')}</span>
            </div>
          )}
          <div className="flex justify-between gap-4">
            <span className="text-emerald-600">FR</span>
            <span className="font-mono">{formatCurrency(point._fr_eur || 0, 'EUR')}</span>
          </div>
          {point._fr_eur_margin != null && (
            <div className="flex justify-between gap-4">
              <span className="text-emerald-600/60 text-xs ml-2">margin</span>
              <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._fr_eur_margin, 'EUR')}</span>
            </div>
          )}
          <div className="flex justify-between gap-4 border-t pt-1 mt-1">
            <span className="text-muted-foreground">Combined (approx)</span>
            <span className="font-mono">{formatCurrency(point._combined || 0, 'GBP')}</span>
          </div>
          {point._combined_margin != null && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground text-xs ml-2">margin</span>
              <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._combined_margin, 'GBP')}</span>
            </div>
          )}
        </div>
      );
    };

    // Simple single-series tooltip
    const SimpleChartTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
          <p className="font-semibold mb-1">{label}</p>
          {payload.map((entry) => (
            <div key={entry.name} className="flex justify-between gap-4">
              <span style={{ color: entry.color }}>{entry.name}</span>
              <span className="font-mono">{formatCurrency(entry.value, entry.name.includes('FR') ? 'EUR' : 'GBP')}</span>
            </div>
          ))}
        </div>
      );
    };

    // Nightly rate tooltip
    const NightlyRateTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
          <p className="font-semibold mb-1">{label}</p>
          {payload.map((entry) => (
            entry.value != null && (
              <div key={entry.name} className="flex justify-between gap-4">
                <span style={{ color: entry.color }}>{entry.name}</span>
                <span className="font-mono">{formatCurrency(entry.value, 'GBP')}/night</span>
              </div>
            )
          ))}
        </div>
      );
    };

    // Subtitle text
    const subtitleText = (() => {
      const total = properties.length;
      if (portfolioRegion === 'uk') return `${total} propert${total === 1 ? 'y' : 'ies'} in 🇬🇧 UK`;
      if (portfolioRegion === 'fr') return `${total} propert${total === 1 ? 'y' : 'ies'} in 🇫🇷 France`;
      const ukCount = properties.filter((p) => p.region === 'uk').length;
      const frCount = properties.filter((p) => p.region === 'fr').length;
      return `${total} properties across 🇬🇧 UK (${ukCount}) and 🇫🇷 France (${frCount})`;
    })();

    return (
      <DashboardLayout>
        <div className="space-y-8" data-testid="dashboard-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Property Portfolio</h1>
              <p className="text-muted-foreground mt-1">{subtitleText}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setIsRefreshing(true); fetchData(); }}
                disabled={isRefreshing}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button onClick={() => setPortfolioYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-semibold w-12 text-center">{portfolioYear}</span>
                <button
                  onClick={() => setPortfolioYear((y) => y + 1)}
                  disabled={portfolioYear >= new Date().getFullYear()}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Region Tabs */}
          <Tabs value={portfolioRegion} onValueChange={setPortfolioRegion}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="fr">{'🇫🇷'} France</TabsTrigger>
              <TabsTrigger value="uk">{'🇬🇧'} UK</TabsTrigger>
            </TabsList>

            <TabsContent value={portfolioRegion}>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                <StatCard
                  title="Booking Revenue"
                  value={formatCurrency(
                    portfolioRegion === 'uk' ? (totals?.uk?.revenue || 0)
                    : portfolioRegion === 'fr' ? (totals?.fr?.revenue || 0)
                    : (totalRevGbp || 0),
                    portfolioRegion === 'fr' ? 'EUR' : 'GBP'
                  )}
                  icon={TrendingUp}
                  subStats={
                    portfolioRegion === 'all' ? [
                      { label: 'UK', flag: '🇬🇧', value: formatCurrency(totals?.uk?.revenue || 0, 'GBP') },
                      { label: 'FR', flag: '🇫🇷', value: formatCurrency(totals?.fr?.revenue || 0, 'EUR') },
                    ] : [
                      { label: 'Bookings', flag: '', value: `${totalBookings} bookings` },
                    ]
                  }
                />
                <StatCard
                  title="Booking Margin"
                  value={formatCurrency(
                    portfolioRegion === 'uk' ? (totals?.uk?.booking_margin || 0)
                    : portfolioRegion === 'fr' ? (totals?.fr?.booking_margin || 0)
                    : (totalMarginGbp || 0),
                    portfolioRegion === 'fr' ? 'EUR' : 'GBP'
                  )}
                  icon={BarChart3}
                  subStats={[
                    { label: 'Margin', flag: '', value: `${marginPct}%` },
                  ]}
                />
                <StatCard
                  title="Total Bookings"
                  value={totalBookings.toLocaleString()}
                  icon={Calendar}
                  subStats={[
                    { label: 'Nights', flag: '', value: `${totalNights.toLocaleString()} nights` },
                  ]}
                />
                <StatCard
                  title="Properties"
                  value={properties.length}
                  icon={Home}
                  subStats={
                    portfolioRegion === 'all' ? [
                      { label: 'UK', flag: '🇬🇧', value: String(properties.filter((p) => p.region === 'uk').length) },
                      { label: 'FR', flag: '🇫🇷', value: String(properties.filter((p) => p.region === 'fr').length) },
                    ] : [
                      { label: portfolioRegion === 'uk' ? 'UK' : 'FR', flag: portfolioRegion === 'uk' ? '🇬🇧' : '🇫🇷', value: String(properties.length) },
                    ]
                  }
                />
                <StatCard
                  title="Avg Nightly Rate"
                  value={totalNights > 0 ? formatCurrency(
                    properties.reduce((s, p) => s + (
                      portfolioRegion === 'all'
                        ? (p.totals?.revenue_gbp_approx || p.totals?.revenue_native || 0)
                        : (p.totals?.revenue_native || 0)
                    ), 0) / totalNights,
                    portfolioRegion === 'fr' ? 'EUR' : 'GBP'
                  ) : 'N/A'}
                  icon={Percent}
                  subStats={[
                    { label: '', flag: '', value: portfolioRegion === 'fr' ? 'EUR' : 'GBP approx' },
                  ]}
                />
                <StatCard
                  title="Revenue / Property"
                  value={properties.length > 0 ? formatCurrency(
                    (portfolioRegion === 'uk' ? (totals?.uk?.revenue || 0)
                    : portfolioRegion === 'fr' ? (totals?.fr?.revenue || 0)
                    : totalRevGbp) / properties.length,
                    portfolioRegion === 'fr' ? 'EUR' : 'GBP'
                  ) : 'N/A'}
                  icon={Home}
                  subStats={[
                    { label: '', flag: '', value: 'avg per property' },
                  ]}
                />
              </div>

              {/* Revenue Chart */}
              {portfolioRevenueChartData.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Monthly Booking Revenue ({portfolioRegion === 'all' ? 'GBP approx' : chartCurrency})
                    </CardTitle>
                    <ForecastCaveat show={hasForecast} />
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={portfolioRevenueChartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickFormatter={(v) => {
                            const sym = chartCurrency === 'EUR' ? '€' : '£';
                            return `${sym}${(v / 1000).toFixed(0)}k`;
                          }}
                        />

                        {portfolioRegion === 'all' ? (
                          <>
                            <Tooltip content={<StackedRevenueTooltip />} />
                            <Legend />
                            <Bar dataKey="UK (GBP)" stackId="revenue" fill="#3B82F6" radius={[0, 0, 0, 0]} shape={<ForecastBarShape data={portfolioRevenueChartData} />}>
                              <LabelList
                                dataKey="UK (GBP)"
                                position="center"
                                formatter={(v) => v >= 1000 ? formatCompact(v, 'GBP') : ''}
                                style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                              />
                            </Bar>
                            <Bar dataKey="FR (GBP approx)" stackId="revenue" fill="#10B981" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={portfolioRevenueChartData} />}>
                              <LabelList
                                dataKey="FR (GBP approx)"
                                position="center"
                                formatter={(v) => v >= 1000 ? formatCompact(v, 'GBP') : ''}
                                style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                              />
                              <LabelList
                                position="top"
                                content={(props) => {
                                  const { x, y, width, index } = props;
                                  const row = portfolioRevenueChartData[index];
                                  const total = (row?.['UK (GBP)'] || 0) + (row?.['FR (GBP approx)'] || 0);
                                  if (!total) return null;
                                  return (
                                    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1f2937">
                                      {formatCompact(total, 'GBP')}
                                    </text>
                                  );
                                }}
                              />
                            </Bar>
                          </>
                        ) : portfolioRegion === 'uk' ? (
                          <>
                            <Tooltip content={<SimpleChartTooltip />} />
                            <Legend />
                            <Bar dataKey="UK (GBP)" fill="#3B82F6" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={portfolioRevenueChartData} />}>
                              <LabelList dataKey="UK (GBP)" position="top" formatter={(v) => formatCompact(v, 'GBP')} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                            </Bar>
                          </>
                        ) : (
                          <>
                            <Tooltip content={<SimpleChartTooltip />} />
                            <Legend />
                            <Bar dataKey="FR (EUR)" fill="#10B981" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={portfolioRevenueChartData} />}>
                              <LabelList dataKey="FR (EUR)" position="top" formatter={(v) => formatCompact(v, 'EUR')} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                            </Bar>
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                    <ForecastTotalsFooter
                      data={portfolioRevenueChartData}
                      series={portfolioRegion === 'all'
                        ? [
                            { key: 'UK (GBP)', label: 'UK', color: '#3B82F6' },
                            { key: 'FR (GBP approx)', label: 'FR', color: '#10B981' },
                          ]
                        : portfolioRegion === 'uk'
                          ? [{ key: 'UK (GBP)', label: 'UK', color: '#3B82F6' }]
                          : [{ key: 'FR (EUR)', label: 'FR', color: '#10B981' }]}
                      currency={chartCurrency}
                      label="Booking Revenue"
                      showCombined={portfolioRegion === 'all'}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Avg Nightly Rate Chart (All tab only) */}
              {portfolioRegion === 'all' && portfolioAvgNightlyRateData.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">Average Net Nightly Rate</CardTitle>
                    <CardDescription>All values in GBP approx. FR rates converted at today's EUR/GBP rate.</CardDescription>
                    <ForecastCaveat show={hasForecast} />
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={portfolioAvgNightlyRateData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickFormatter={(v) => `£${Math.round(v)}`}
                        />
                        <Tooltip content={<NightlyRateTooltip />} />
                        <Legend />

                        <Line type="monotone" dataKey="Overall" stroke="#6B7280" strokeWidth={2.5} dot={{ r: 3 }} connectNulls>
                          <LabelList dataKey="Overall" position="top" formatter={(v) => `£${Math.round(v)}`} style={{ fontSize: 11, fontWeight: 600, fill: '#6B7280' }} offset={8} />
                        </Line>
                        <Line type="monotone" dataKey="Overall_forecast" stroke="#6B7280" strokeWidth={2.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                        <Line type="monotone" dataKey={'🇬🇧 UK'} stroke="#3B82F6" strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey={'🇬🇧 UK_forecast'} stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                        <Line type="monotone" dataKey={'🇫🇷 France'} stroke="#10B981" strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey={'🇫🇷 France_forecast'} stroke="#10B981" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Top Properties */}
              {(topByRevenue.length > 0 || topByMargin.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {topByRevenue.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Top Properties by Revenue</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {topByRevenue.map((p, i) => (
                          <div key={p.property_id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-muted-foreground w-6">{i + 1}</span>
                              <div>
                                <Link to="/real-estate" className="font-medium text-sm hover:underline">{p.name}</Link>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {regionBadge(p.region)}
                                  <span className="text-xs text-muted-foreground">{p.platform}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-semibold text-sm">
                                {formatCurrency(p.totals?.revenue_native || 0, p.currency || 'GBP')}
                              </p>
                              {p.totals?.margin_pct != null && (
                                <p className="text-xs text-muted-foreground">{p.totals.margin_pct}% margin</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {topByMargin.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Top Properties by Margin %</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {topByMargin.map((p, i) => (
                          <div key={p.property_id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-muted-foreground w-6">{i + 1}</span>
                              <div>
                                <Link to="/real-estate" className="font-medium text-sm hover:underline">{p.name}</Link>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {regionBadge(p.region)}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-semibold text-sm text-green-600 dark:text-green-400">
                                {p.totals?.margin_pct}%
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatCurrency(p.totals?.revenue_native || 0, p.currency || 'GBP')} rev
                              </p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Data quality note */}
              {portfolioData.data_quality?.occupancy_data_source === 'listed_since_naive' && (
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Occupancy figures marked "est." use first-known-booking date as a proxy for listing date. Real availability data is pending.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DashboardLayout>
    );
  }

  // All Personal and All Entities aggregates, plus the single-entity view, all
  // render the same detailed dashboard below, differing only by scope (which
  // drives the data) and a few header labels.
  const aggregateTitle = isPersonalAggregate ? 'Personal Dashboard' : 'All Entities';
  const aggregateDot = isPersonalAggregate ? '#8B5CF6' : '#64748B';

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: isAggregateView ? aggregateDot : (entityTypeColorMap[selectedEntityObj?.type] || '#6B7280') }}
              />
              <h1 className="text-3xl font-bold tracking-tight">{isAggregateView ? aggregateTitle : (selectedEntityObj?.name || 'Dashboard')}</h1>
            </div>
            {!isAggregateView && (
              <p className="text-muted-foreground mt-1">
                {selectedEntityObj?.country}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CurrencyToggle />
            {!isAggregateView && (
              <Button variant="outline" onClick={() => setSelectedEntity('all')}>
                View All Entities
              </Button>
            )}
            <Button onClick={handleRefresh} disabled={isRefreshing} data-testid="refresh-dashboard-btn">
              <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        {/* 1. Stat Cards: full set (dashboards round 2 part 3), now reused
            identically for the single-entity view and the All Entities /
            All Personal aggregates -- same entity-pnl / A&L balance-sheet
            canonical predicates either way (get_business_pnl / get_personal_pnl,
            compute_balance_sheet), just summed across the scope's members
            server-side rather than a single entity. This replaces the
            aggregate's former ad-hoc three-card set (a separate
            assetRegisterAPI.summary() computation that could drift from the
            A&L page's own canonical figure -- the exact "hand-maintained
            drift" bug class CLAUDE.md section 7.4 warns about). Property
            Portfolio entities show P&L figures in their own native currency
            (never displayCurrency), same convention the Gross Revenue chart
            uses; the aggregate is always GBP-summed so entityPnl.currency
            resolves to GBP there. Sublines differ where a genuine mix exists
            at aggregate scope: the BR OS/Portfolio revenue/margin/cashflow
            splits -- a single entity is always exactly one P&L source, so
            those splits are aggregate-only and collapse to nothing for a
            single entity (see netRevenueSplit/marginComponentSplit/
            cashflowComponentSplit above). Bank Balance's per-bank breakdown
            and Properties' France/UK split are NOT aggregate-only -- both
            are genuinely meaningful at single-entity scope too (an entity
            can hold accounts at more than one bank, or own properties in
            both regions), so they render at every scope. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Booking Volume"
            value={formatCompact(convertAmount(grossRevenueCard.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)}
            valueTitle={formatCurrencyFull(convertAmount(grossRevenueCard.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)}
            icon={DollarSign}
            subLine={entityPnl ? (isAggregateView ? aggregateRevenueSplit?.text : null) : 'No data'}
            subLineTitle={isAggregateView ? aggregateRevenueSplit?.title : undefined}
          />
          <StatCard
            title="Net Revenue"
            value={formatCompact(convertAmount(netRevenueCard.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)}
            valueTitle={formatCurrencyFull(convertAmount(netRevenueCard.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)}
            icon={DollarSign}
            subLine={entityPnl ? (isAggregateView ? netRevenueSplit?.text : null) : 'No data'}
            subLineTitle={isAggregateView ? netRevenueSplit?.title : undefined}
          />
          <StatCard
            title="Gross Margin"
            value={`${formatCompact(convertAmount(marginCards.grossMargin.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)} · ${marginCards.grossMargin.pct.toFixed(1)}%`}
            valueTitle={`${formatCurrencyFull(convertAmount(marginCards.grossMargin.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)} · ${marginCards.grossMargin.pct.toFixed(1)}%`}
            icon={Percent}
            subLine={entityPnl ? (isAggregateView ? marginComponentSplit?.grossText : null) : 'No data'}
          />
          <StatCard
            title="Net Margin"
            value={`${formatCompact(convertAmount(marginCards.netMargin.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)} · ${marginCards.netMargin.pct.toFixed(1)}%`}
            valueTitle={`${formatCurrencyFull(convertAmount(marginCards.netMargin.headline, entityPnl?.currency || currency, displayCurrency, eurGbpRate), displayCurrency)} · ${marginCards.netMargin.pct.toFixed(1)}%`}
            icon={Percent}
            subLine={entityPnl ? (isAggregateView ? marginComponentSplit?.netText : null) : 'No data'}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Bank Balance"
            value={formatCompact(bankBalanceHeadline, displayCurrency)}
            valueTitle={formatCurrencyFull(bankBalanceHeadline, displayCurrency)}
            icon={Wallet}
            subStats={bankBalanceGroups.map((g) => ({
              label: g.bankName,
              flag: (
                <span className="flex items-center gap-1">
                  <BankLogo bankName={g.bankName} size="sm" />
                  {g.bankName}
                </span>
              ),
              value: formatCompact(g.subtotal, displayCurrency),
              title: formatCurrencyFull(g.subtotal, displayCurrency),
            }))}
          />
          <StatCard
            title="Net Assets"
            value={entityBalanceSheet ? formatCompact(netAssetsCard.headline, 'GBP') : 'No data'}
            valueTitle={entityBalanceSheet ? formatCurrencyFull(netAssetsCard.headline, 'GBP') : undefined}
            icon={Landmark}
            // Aggregate-only: opens the Asset Register dialog, same as the
            // card it replaces. Pre-existing limitation, unchanged by this
            // consolidation: the dialog requires a concrete entityId, which
            // is null for both aggregates, so the add-flow itself still
            // needs a per-entity picker before it's actually usable here.
            onClick={isAggregateView ? () => setAssetDialogOpen(true) : undefined}
            subLine={
              entityBalanceSheet
                ? `Fixed ${formatCompact(netAssetsCard.fixed, 'GBP')} · Current ${formatCompact(netAssetsCard.current, 'GBP')}`
                : 'No data'
            }
            subLineTitle={
              entityBalanceSheet
                ? `Fixed ${formatCurrencyFull(netAssetsCard.fixed, 'GBP')} · Current ${formatCurrencyFull(netAssetsCard.current, 'GBP')}`
                : undefined
            }
          />
          {propertiesCard.hasProperties && (
            <StatCard
              title="Properties"
              value={String(propertiesCard.headline)}
              icon={Home}
              subLine={propertiesCountrySplit}
            />
          )}
          <StatCard
            title="Avg Net Cashflow (YTD, complete months)"
            value={cashRunway
              ? formatCompact(
                  computeAvgNetCashflow(
                    monthlyCashflowChartData,
                    convertAmount(parseFloat(cashRunway.avg_monthly_net || 0), entityPnl?.currency || currency, displayCurrency, eurGbpRate),
                  ),
                  displayCurrency,
                )
              : 'No data'}
            valueTitle={cashRunway
              ? formatCurrencyFull(
                  computeAvgNetCashflow(
                    monthlyCashflowChartData,
                    convertAmount(parseFloat(cashRunway.avg_monthly_net || 0), entityPnl?.currency || currency, displayCurrency, eurGbpRate),
                  ),
                  displayCurrency,
                )
              : undefined}
            subLine={cashRunway ? (isAggregateView ? cashflowComponentSplit?.text : null) : undefined}
            subLineTitle={isAggregateView ? cashflowComponentSplit?.title : undefined}
            icon={Clock}
          />
        </div>

        {/* 2. Alerts (full width) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Recent Alerts
            </CardTitle>
            <CardDescription>
              {alerts.filter((a) => !a.is_read).length} unread notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertsList alerts={alerts} />
          </CardContent>
        </Card>

        {/* 3. Main chart: full page width (redesign step 3, rework of the
            former "Income, Expenses & Net Income" chart), now shared by the
            single-entity view and the All Entities / All Personal
            aggregates -- entityGrossRevenueChartData is built from the same
            entityPnl the stat cards above use, so it's already populated
            for both. Bank Accounts pairs with the Bank Balance chart and
            Cash Runway below (redesign step 5), also unified across scopes. */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                Booking Volume (monthly) {new Date().getFullYear()}
                {isAggregateView && selectedScope === 'entities' && (entityPnl?.unattributed_properties?.count ?? 0) > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                    title={`${entityPnl.unattributed_properties.count} propert${entityPnl.unattributed_properties.count === 1 ? 'y' : 'ies'} without a single confidently-resolved entity (${entityPnl.unattributed_properties.names.join(', ')}) -- missing, or claimed by more than one -- so ${formatCurrencyFull(entityPnl.unattributed_properties.revenue, entityPnl.currency)} is excluded from every entity's total, not guessed`}
                  >
                    {entityPnl.unattributed_properties.count} unattributed
                  </Badge>
                )}
              </CardTitle>
              <ForecastCaveat show={entityGrossRevenueChartData.some((d) => d._isForecast)} />
            </CardHeader>
            <CardContent>
              {entityPnl && entityGrossRevenueChartData.length > 0 ? (() => {
                const chartData = entityGrossRevenueChartData;
                const ytd = computeEntityGrossRevenueYtd(chartData);
                // chartData (entityGrossRevenueChartData) is already
                // converted to displayCurrency in its own memo above.
                const chartCurrency = displayCurrency;
                const lastLabel = revenueSegments[revenueSegments.length - 1].label;
                const segmentLabelFormatter = (v) => (v > 0 ? formatCompact(v, chartCurrency) : '');
                return (
                  <>
                    <ResponsiveContainer width="100%" height={380}>
                      <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis
                          tickFormatter={(v) => formatCompact(v, chartCurrency)}
                          tick={{ fontSize: 11 }}
                          width={60}
                        />
                        <Tooltip
                          formatter={(val, name) => [formatCurrencyFull(val, chartCurrency), name]}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {/* Actual bars: BR OS (bottom), Portfolio (top), same
                            order as the group chart. Each segment carries its
                            own value label; the stacked TOTAL rides whichever
                            segment is the topmost non-zero one for that month
                            (_carrier), read from chartData by index so the
                            label only appears on actual (non-forecast) months. */}
                        {revenueSegments.map((seg) => (
                          <Bar key={seg.key} dataKey={seg.label} stackId="grossRevenue" fill={seg.color}
                            radius={seg.label === lastLabel ? [2, 2, 0, 0] : [0, 0, 0, 0]}>
                            <LabelList dataKey={seg.label} position="inside" fontSize={10}
                              formatter={segmentLabelFormatter} fill="#fff" />
                            <LabelList
                              position="top"
                              content={(lp) => {
                                const { x, y, width, index } = lp;
                                const row = chartData[index];
                                if (!row || row._carrier !== seg.key || !row._total) return null;
                                return (
                                  <Text x={x + width / 2} y={y - 7} textAnchor="middle"
                                    verticalAnchor="end" fontSize={11} fontWeight={700}
                                    fill="hsl(var(--foreground))">
                                    {formatCompact(row._total, chartCurrency)}
                                  </Text>
                                );
                              }}
                            />
                          </Bar>
                        ))}
                        {/* Forecast segments (BR OS and Portfolio), never summed
                            into the actuals bars/total, visually distinct
                            (lighter fill, dashed outline). Only drawn when the
                            series has data, so a BR OS entity shows its
                            forward overlay and no empty Portfolio legend. */}
                        {revenueSegments.map((seg) => (
                          chartData.some((d) => (d[`${seg.label}_forecast`] || 0) > 0) ? (
                            <Bar key={`${seg.key}_forecast`} dataKey={`${seg.label}_forecast`}
                              name={`${seg.label} (forecast)`} stackId="grossRevenue"
                              fill={seg.color} fillOpacity={0.35} stroke={seg.color} strokeDasharray="4 2" strokeWidth={1}
                              radius={[2, 2, 0, 0]}>
                              <LabelList dataKey={`${seg.label}_forecast`} position="inside" fontSize={10}
                                formatter={segmentLabelFormatter} fill={seg.color} />
                              {/* Stacked forecast TOTAL, lib/entityGrossRevenueChart.js
                                  annotateForecastTotals. */}
                              <LabelList position="top"
                                content={forecastTotalLabelContent(chartData, `${seg.label}_forecast`,
                                  { formatter: (v) => formatCompact(v, chartCurrency), fill: seg.color })} />
                            </Bar>
                          ) : null
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
                      <span>YTD Booking Volume:</span>
                      {revenueSegments.map((seg) => (
                        <span key={seg.key} className="inline-flex items-center gap-1">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                          <span>{seg.label}</span>
                          <span className="font-mono font-semibold text-foreground">
                            {formatCompact(ytd[seg.label], chartCurrency)}
                          </span>
                        </span>
                      ))}
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
                        <span>Combined</span>
                        <span className="font-mono font-semibold text-foreground">
                          {formatCompact(ytd._combined, chartCurrency)}
                        </span>
                      </span>
                    </div>
                    <ForecastTotalsFooter
                      data={chartData}
                      series={revenueSegments.map((seg) => ({
                        key: seg.label, label: seg.label, color: seg.color,
                      }))}
                      currency={chartCurrency}
                      label="Booking Volume"
                    />
                  </>
                );
              })() : (
                <div className="text-center py-12 text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No P&L data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Margin (monthly): Net Margin stacked BR OS + Portfolio, Gross
              Margin combined line -- directly below Gross Revenue, same
              BR OS/Portfolio segment split as that chart. */}
          <MarginChart
            chartData={entityMarginChartData}
            segments={revenueSegments}
            displayCurrency={displayCurrency}
            year={new Date().getFullYear()}
          />

        {/* Redesign step 5, shared by both scopes now: FX Exposure gets its
            own row above Bank Accounts + Cash Runway. */}
        <FxExposureCard fxExposure={fxExposure} currency={currency} formatCurrency={formatCurrency} />

        {/* Cash Runway | Bank Accounts (dashboards round 3 layout: the ONLY
            place Cash Runway appears on this dashboard). Bank Accounts stays
            default-collapsed to one line per bank; expand a bank to see its
            individual accounts (dashboards round 2, part 3). */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cash Runway. avg_monthly_net comes back in the entity's own
                  native currency (raw transaction sum, no FX conversion);
                  convert it to displayCurrency so it never gets mislabeled
                  next to bankBalanceHeadline (which IS displayCurrency-
                  converted already, same as the Bank Balance card). */}
              <CashRunwayCard
                cashRunway={
                  cashRunway && (entityPnl?.currency || currency) !== displayCurrency
                    ? { ...cashRunway, avg_monthly_net: convertAmount(parseFloat(cashRunway.avg_monthly_net || 0), entityPnl?.currency || currency, displayCurrency, eurGbpRate) }
                    : cashRunway
                }
                currentCash={bankBalanceHeadline}
                currency={displayCurrency}
                formatCurrency={formatCurrency}
              />

              {bankSummaryData && bankSummaryData.accounts && bankSummaryData.accounts.length > 0 ? (() => {
                  // fx_rates_spot (unrounded), not fx_rates (2dp-quantized) --
                  // see bankBalanceGroups above for why.
                  const fxRates = bankSummaryData.fx_rates_spot || {};
                  const groups = groupAccountsByBank(bankSummaryData.accounts, displayCurrency, fxRates);
                  // Sum of the same per-bank subtotals rendered below, not a
                  // separately fetched/derived figure, so this row reconciles
                  // to the rows above (and to bankBalanceHeadline, which is
                  // computed the same way) by construction -- never a second
                  // live source.
                  const groupsTotal = groups.reduce((sum, g) => sum + g.subtotal, 0);
                  return (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-primary" />
                          Bank Accounts
                        </CardTitle>
                        <CardDescription>
                          {groups.length} bank{groups.length !== 1 ? 's' : ''} &middot; {bankSummaryData.accounts.length} account{bankSummaryData.accounts.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          {groups.map(function (group) {
                            return (
                              <SharedBankGroup
                                key={group.bankName}
                                bankName={group.bankName}
                                accounts={group.accounts}
                                subtotal={group.subtotal}
                                displayCurrency={displayCurrency}
                                fxRates={fxRates}
                                defaultExpanded={false}
                              />
                            );
                          })}
                        </div>
                        <div
                          className="flex items-center justify-between pt-2.5 mt-1.5 border-t border-border/60"
                          data-testid="bank-accounts-total-row"
                        >
                          <span className="text-sm font-bold pl-3">Total</span>
                          <span className="text-sm font-mono font-bold whitespace-nowrap">
                            {/* formatCurrencyFull, not formatCurrency (aliased to
                                formatCurrencyShort, which Math.abs()-strips the
                                sign) -- matches SharedBankGroup's own formatter for
                                the rows immediately above, and never hides a
                                negative (overdrawn) total as a positive number. */}
                            {formatCurrencyFull(groupsTotal, displayCurrency)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })() : <div />}
        </div>

        {/* Bank Balance chart (dashboards round 3): calendar-year Line
            chart, full width, shared with the Banking page. */}
        <BankBalanceHistoryChart
          rows={bankBalanceHistoryChartData.rows}
          series={bankBalanceHistoryChartData.series}
          displayCurrency={displayCurrency}
          unknownBankAccounts={entityBankHistory?.unknown_bank_accounts || []}
        />

        {/* Net Assets: calendar-year + 3-month forecast, full width. */}
        {netAssetsChartData.length > 0 && (
          <NetAssetsChart
            chartData={netAssetsChartData}
            displayCurrency={displayCurrency}
          />
        )}

        {/* Monthly Cashflow: calendar-year, full width. */}
        {monthlyCashflowChartData.length > 0 && (
          <MonthlyCashflowChart
            chartData={monthlyCashflowChartData}
            displayCurrency={displayCurrency}
          />
        )}

        {/* Cashflow Trend (full width, aggregate view only -- deleted from
            the single-entity dashboard, redesign step 5; the underlying
            endpoint stays live for other consumers). */}
        {isAggregateView && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Cashflow Trend
            </CardTitle>
            <CardDescription>Inflow vs Outflow over the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <CashflowChart data={cashflowData} currency={currency} />
          </CardContent>
        </Card>
        )}

        {/* Quick Stats -- aggregate view only. Deleted from the single-entity
            dashboard (redesign step 2): Pending/Overdue invoices, Unreconciled,
            Suggested matches don't belong on a per-entity financial overview. */}
        {isAggregateView && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Invoices</p>
                    <p className="text-2xl font-bold mt-1">{summary?.invoices?.pending || 0}</p>
                    {summary?.invoices?.unpaid_by_currency && Object.keys(summary.invoices.unpaid_by_currency).length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {Object.entries(summary.invoices.unpaid_by_currency).map(([cur, amt]) => {
                          const sym = CURRENCY_SYMBOLS[cur] || cur;
                          return `${sym}${amt.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
                        }).join(' · ')}
                      </p>
                    )}
                  </div>
                  <FileText className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Overdue Invoices</p>
                    <p className="text-2xl font-bold mt-1 text-red-500">{summary?.invoices?.overdue || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Unreconciled</p>
                    <p className="text-2xl font-bold mt-1">{summary?.transactions?.unreconciled || 0}</p>
                  </div>
                  <ArrowLeftRight className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Suggested Matches</p>
                    <p className="text-2xl font-bold mt-1 text-primary">{summary?.matches?.suggested || 0}</p>
                  </div>
                  <ArrowLeftRight className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Fan, 2026-09-03: "IT NEEDS TO BE IN THE ENTITIES PAGE". Same month x
            country reserve/profit table as BR OS and the group page. Entities
            scope only - it reads BROS reserve entries, so it would be
            misleading under personal or portfolio scope. */}
        {selectedScope === 'entities' && (
          <div className="mt-6">
            <TaxReserveCountryMonths />
          </div>
        )}
      </div>

      {/* Asset Register Dialog */}
      <AssetRegisterDialog
        open={assetDialogOpen}
        onOpenChange={setAssetDialogOpen}
        entityId={selectedEntityObj?.id}
        defaultCurrency={currency}
        onUpdate={handleAssetRegisterUpdate}
      />
    </DashboardLayout>
  );
}
